import { fromJS, is } from 'immutable'
import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { replace, LOCATION_CHANGE } from 'react-router-redux'
import { captureException } from 'utils/SentryHelpers'
import * as RouterSelectors from 'app/modules/router/selectors'
import { SearchViewEnum } from 'constants/SearchViews'
import * as EmbedActions from 'app/modules/embed/actions'
import * as EmbedSelectors from 'app/modules/embed/selectors'
import { api } from './api'
import { shouldRedirectToInbox } from './helpers'
import { INBOX_REGEX } from './constants'
import * as actions from './actions'
import * as selectors from './selectors'
import * as MessageActions from 'app/modules/message/actions'
import * as UserSelectors from 'app/modules/user/selectors'
import * as GateSelectors from 'app/modules/gates/selectors'
import { hasConversationAccess } from 'app/modules/access/helpers'
import { Inbox } from './models'
import { getFilters as getCachedFilters, getInboxId as getCachedInboxId, clearAll } from '../../cache/filters'
import * as UserPropertiesSelectors from 'app/modules/user/properties/selectors'
import * as BulkUpdateConversationActions from "../conversation/bulkUpdate/actions"

function inboxIdFromPathName(pathname) {
  const result = INBOX_REGEX.exec(pathname)

  if (result) {
    return parseInt(result[1])
  }
}

export function inboxQueryChanged(action$) {
  return action$
    .ofType(LOCATION_CHANGE)
    .mergeMap(action => {
      const inboxId = inboxIdFromPathName(action.payload.pathname)
      if (inboxId) {
        return Observable.of({ inboxId, query: fromJS(action.payload.query) })
      } else {
        return Observable.empty()
      }
    })
    .map(actions.inboxChanged)
}

const patchInbox = action$ => {
  return action$.ofType(actions.patchInbox.toString())
    .pluck('payload')
    .mergeMap(({ id, name, address }) => {
      return api
        .patchInbox({ id, name, address })
        .map(actions.patchInboxSuccess)
        .catch(error => {
          captureException(error)
          return Observable.of(actions.patchInboxFailed({ id, error }))
        })
  })
}

const archiveInbox = action$ => {
  return action$.ofType(actions.archiveInbox.toString())
    .pluck('payload')
    .mergeMap(({ id }) => {
      return api
        .archiveInbox({ id })
        .map(actions.archiveInboxSuccess)
        .catch(error => {
          captureException(error)
          return Observable.of(actions.archiveInboxFailed({ id, error }))
        })
    })
}

function bulkCloseForInbox(action$) {
  return action$.ofType(actions.bulkCloseForInbox.toString()).pluck('payload').mergeMap((inboxId) => {
    return api
      .bulkCloseForInbox(inboxId)
      .map(actions.bulkCloseForInboxRequested)
      .catch(error => {
        captureException(error)
        return Observable.of(actions.bulkCloseForInboxFailed({ inboxId, error }))
      })
  })
}

function fetchInbox(action$, { getState }) {
  return action$
    .ofType(actions.fetchInbox.toString())
    .map(action => action.payload)
    .filter(({ id, force }) => !selectors.inbox(id)(getState()) || force)
    .mergeMap(({ id }) => {
      return api
        .fetchInbox(id)
        .takeUntil(
          action$.ofType(actions.fetchInboxCancelled.toString()).filter(action => action.payload.id === id)
        )
        .map(actions.receiveInbox)
        .startWith(actions.fetchInboxPending({ inboxId: id }))
        .catch(error => {
          captureException(error)
          return Observable.of(actions.fetchInboxFailed({ inboxId: id, error }))
        })
    })
}

const fetchWhitelabel = action$ =>
  action$
    .ofType(actions.fetchWhitelabel.toString())
    .pluck('payload')
    .mergeMap(({ domain }) => {
      return api
        .fetchWhitelabel(domain)
        .map(actions.fetchWhitelabelSuccess)
        .startWith(actions.fetchWhitelabelPending({ domain }))
        .catch(err => {
          if (err && err.status && err.status === 404) {
            return Observable.of(actions.fetchWhitelabelFailed({ domain, missing: true }))
          }
          captureException(err)
          return Observable.of(actions.fetchWhitelabelFailed({ domain, missing: false }))
        })
    })

const createInboxBeforeWhitelabel = action$ =>
  action$
    .ofType(actions.createInboxBeforeWhitelabel.toString())
    .pluck('payload')
    .mergeMap(({ address, name, domain, onSuccess = () => {}, onFail = () => {} }) =>
      api
        .createInbox({ address, name })
        .map(() => actions.createWhitelabel({ domain, onSuccess, onFail }))
        .startWith(actions.createWhitelabelPending({ domain }))
        .catch(err => {
          onFail()
          captureException(err, { extra: { address, name, domain }})
          return Observable.of(actions.createWhitelabelFailed({ domain, err }))
        })
    )

const createWhitelabel = action$ =>
  action$
    .ofType(actions.createWhitelabel.toString())
    .pluck('payload')
    .mergeMap(({ domain, onSuccess, onFail }) =>
      api
        .createWhitelabel(domain)
        .map(res => {
          onSuccess()
          return actions.createWhitelabelSuccess(res)
        })
        .startWith(actions.createWhitelabelPending({ domain }))
        .catch(err => {
          captureException(err, { extra: { domain }})
          onFail()
          return Observable.of(actions.createWhitelabelFailed({ domain, err }))
        })
    )

const verifyWhitelabel = action$ =>
  action$
    .ofType(actions.verifyWhitelabel.toString())
    .pluck('payload')
    .mergeMap(({ domain }) =>
      api
        .verifyWhitelabel(domain)
        .map(res => {
          const success = res.get('dns').every(record => record.get('valid'))
          if (success) {
            return actions.verifyWhitelabelSuccess({ domain, whitelabel: res })
          } else {
            return actions.verifyWhitelabelFailed({ domain, whitelabel: res })
          }
        })
        .startWith(actions.verifyWhitelabelPending({ domain }))
        .catch(err => {
          // TODO: should this be different..?
          return Observable.of(actions.verifyWhitelabelFailed({ domain, err }))
        })
    )

const createInbox = action$ =>
  action$
    .ofType(actions.createInbox.toString())
    .pluck('payload')
    .mergeMap(({ address, name }) =>
      api
        .createInbox({ address, name })
        .map(res => actions.createInboxSuccess(Inbox(res)))
        .startWith(actions.createInboxPending())
        .catch(err => Observable.of(actions.createInboxFailed({ err, address, name })))
    )

function fetchCounts(action$, { getState }) {
  return action$
    .ofType(actions.fetchCounts.toString())
    .pluck('payload')
    .switchMap(({ id }) => {
      const state = getState()
      if (GateSelectors.hasInboxFilterSidebar(state) && (
        UserPropertiesSelectors.properties(state).get('is_opted_in_to_modern_conversation_ui')
      )) {
        return api
          .fetchCounts2(id)
          .map(counts => actions.receiveCounts2({ id, counts }))
          .startWith(actions.fetchCountsPending({ id }))
          .catch(error => {
            captureException(error)
            if (error.status > 400 && error.status < 500) {
              clearAll()
              return Observable.of(replace({ pathname: '/inboxes' }))
            }

            return Observable.of(actions.fetchCountsFailed({ id, error }))
          })
      } else {
        return api
          .fetchCounts(id)
          .map(counts => actions.receiveCounts({ id, counts }))
          .startWith(actions.fetchCountsPending({ id }))
          .catch(error => {
            captureException(error)
            if (error.status > 400 && error.status < 500) {
              clearAll()
              return Observable.of(replace({ pathname: '/inboxes' }))
            }

            return Observable.of(actions.fetchCountsFailed({ id, error }))
          })
      }
    })
}

function fetchCountsForInboxIds(action$) {
  return action$
    .ofType(actions.fetchCountsForInboxIds.toString())
    .pluck('payload')
    .switchMap(({ ids }) => {
      return Observable.from(ids).mergeMap(id => {
        return api
          .fetchCounts(id)
          .map(counts => actions.receiveCounts({ id, counts }))
          .startWith(actions.fetchCountsPending({ id }))
          .catch(error => {
            captureException(error)
            return Observable.of(actions.fetchCountsFailed({ id, error }))
          })
      })
    })
}

function fetchInboxList(action$) {
  return action$.ofType(actions.fetchInboxList.toString()).exhaustMap(() => {
    return api
      .fetchInboxList()
      .takeUntil(action$.ofType(actions.fetchInboxListCancelled.toString()))
      .map(actions.receiveInboxList)
      .startWith(actions.fetchInboxListPending())
      .catch(error => {
        captureException(error)
        return Observable.of(actions.fetchInboxListFailed(error))
      })
  })
}

function fetchSendingDomains(action$) {
  return action$.ofType(actions.fetchSendingDomains.toString()).exhaustMap(() => {
    return api
      .fetchSendingDomains()
      .startWith(actions.fetchSendingDomainsPending())
      .catch(error => {
        captureException(error)
      })
  })
}

function conversationEvent(action$, { getState }) {
  return action$
    .ofType(MessageActions.receiveMessage.toString())
    .pluck('payload', 'message')
    .filter(({ type }) => type === 'CONVERSATION_EVENT')
    .filter(({ conversationEvent: { type }}) => {
      return type === 'ADDED_PARTICIPANTS' || type === 'REMOVED_PARTICIPANTS'
    })
    .filter(message => {
      const currentUser = UserSelectors.currentUser(getState())
      const {
        conversationEvent: { currentParticipantIds: currentParticipants, participantIds: participants },
      } = message
      return (
        hasConversationAccess(currentUser, participants) ||
        hasConversationAccess(currentUser, currentParticipants)
      )
    })
    .map(message => {
      const {
        inboxId,
        conversationEvent: {
          currentParticipantIds: currentParticipants,
          participantIds: participants,
          conversationStatus,
          type,
        },
      } = message
      return actions.changeParticipants({
        inboxId,
        currentParticipants,
        participants,
        conversationStatus,
        isAdd: type === 'ADDED_PARTICIPANTS',
        currentUserId: UserSelectors.currentUser(getState()).id,
      })
    })
}

function defaultInboxRedirect(action$, { getState }) {
  let originalQueryParams = null
  const navigatedToInboxesWithoutId$ = action$
    .ofType(LOCATION_CHANGE)
    .pluck('payload')
    .filter(({ pathname }) => shouldRedirectToInbox(pathname))

  const fetchEmbed$ = navigatedToInboxesWithoutId$.first().map(payload => {
    originalQueryParams = payload.query
    return EmbedActions.fetchEmbed()
  })

  const redirectOnEmbedSuccess$ = action$
    .ofType(EmbedActions.fetchEmbedSuccess.toString())
    .map(() => actions.inboxRedirect())

  const redirectToDefaultInbox$ = action$.ofType(actions.inboxRedirect.toString()).switchMap(() => {
    if (!shouldRedirectToInbox(RouterSelectors.pathname(getState()))) {
      return Observable.empty()
    }

    const defaultInboxId = EmbedSelectors.defaultInboxId(getState())
    const savedInbox = getCachedInboxId()
    const inboxId = savedInbox || defaultInboxId

    const pathname = `/inboxes/${inboxId}/conversations`

    const storedFiltersQuery = getCachedFilters()
    const defaultFiltersQuery = SearchViewEnum.open.value.getQuery()
    const query = storedFiltersQuery || defaultFiltersQuery

    const mergedQueryParams = { ...query, ...originalQueryParams }
    originalQueryParams = null
    return Observable.of(replace({ pathname, query: mergedQueryParams }))
  })

  return Observable.merge(fetchEmbed$, redirectOnEmbedSuccess$, redirectToDefaultInbox$)
}

const receiveBulkUpdateSuccess = (action$, { getState }) => {
  return action$
    .ofType(BulkUpdateConversationActions.receiveBulkUpdateSuccess.toString())
    .pluck('payload')
    .filter(({ inboxId }) => inboxId === selectors.currentInboxId(getState()))
    .map(({ inboxId, conversationDiffs }) => {
      const currentUser = UserSelectors.currentUser(getState())

      const filteredConversationDiffs = conversationDiffs.filter(({ currentParticipantIds }) => {
        return hasConversationAccess(currentParticipantIds, currentUser)
      })

      const updates = filteredConversationDiffs.map(({
        currentStatus,
        previousStatus,
        currentParticipantIds
      }) => ({
        currentParticipants: currentParticipantIds,
        previousStatus,
        status: currentStatus,
      }))

      return actions.bulkChangeStatus({
        id: inboxId,
        currentUserId: currentUser.id,
        conversationDiffs: updates,
      })
    })
}

export const epic = combineEpics(
  fetchInbox,
  fetchInboxList,
  defaultInboxRedirect,
  fetchCounts,
  fetchCountsForInboxIds,
  fetchSendingDomains,
  bulkCloseForInbox,
  inboxQueryChanged,
  conversationEvent,
  fetchWhitelabel,
  createWhitelabel,
  verifyWhitelabel,
  createInbox,
  createInboxBeforeWhitelabel,
  receiveBulkUpdateSuccess,
  patchInbox,
  archiveInbox
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/inbox/epic.js